import {
  COMPANY_ADDRESS,
  COMPANY_LEGAL_NAME,
  PRODUCT_NAME,
  SUPPORT_EMAIL
} from "../lib/brand";
import LegalPage, { Section } from "../components/LegalPage";
import {
  FREE_AGENT_LIMIT,
  FREE_HISTORY_DAYS,
  FREE_ANSWERS_PER_MONTH,
  FREE_QUESTIONS_PER_SESSION,
  ENTERPRISE,
  PAID_PLAN_IDS,
  PLANS,
  answers,
  usd, EARLY_ACCESS } from "../lib/plans";

const EFFECTIVE = "November 3, 2026";

export default function Terms() {
  return (
    <LegalPage title="Terms of Use" effective={EFFECTIVE}>
      <Section heading="1. Acceptance of terms">
        <p>
          {PRODUCT_NAME} is operated by {COMPANY_LEGAL_NAME} ("we", "us"). By creating an account or
          using the service you agree to these terms. If you are agreeing on behalf of a company, you
          confirm you are authorized to bind it.
        </p>
      </Section>

      <Section heading="2. What the service does">
        <p>
          {PRODUCT_NAME} builds and hosts chat assistants that answer questions using content you
          supply: documents, question-and-answer pairs, text and web pages you point us at. The
          assistants run on Google Cloud, using Google's Gemini models on Vertex AI. They can be
          embedded on your own website, or opened from a hosted page at a short link and its QR code, which you may
          share anywhere, including business cards, signage, email signatures and social profiles.
        </p>
      </Section>

      <Section heading="3. Your content and your responsibility for it">
        <p>
          You keep ownership of everything you upload. You grant us the license we need to store it,
          index it with Google Cloud, and use it to answer your visitors' questions, and nothing more.
          We do not use your content to train models for anyone else.
        </p>
        <p>
          You are responsible for having the right to upload what you upload, and for what your
          assistant tells your visitors. Do not upload content you do not have the rights to, and do
          not use the service to collect sensitive personal data, health records, payment card
          numbers, or anything subject to HIPAA, PCI-DSS or similar regimes.
        </p>
        <p>
          If you connect your assistant to your own systems, such as a booking tool, Slack, Discord,
          a webhook or your own API, you are responsible for those systems and for choosing what the assistant
          is allowed to do with them.
        </p>
        <p>
          If you or your team take over a conversation and reply to a visitor yourselves, you are
          responsible for what you write to them.
        </p>
      </Section>

      <Section heading="4. Generated answers are not guaranteed">
        <p>
          Assistants generate answers from your content using a large language model. They can be
          wrong, incomplete or out of date, and they can fail to answer at all. You are responsible
          for reviewing what your assistant says and for any reliance your visitors place on it. Do
          not use {PRODUCT_NAME} where an incorrect answer could cause physical, financial, legal or
          medical harm.
        </p>
        <p>
          Price estimates given in a chat, by our own assistant or by yours, are rough, non-binding
          estimates worked out from a price list and stated assumptions. They are not quotes, offers
          or commitments to sell at that price, and each one says so. Prices are agreed only in
          writing. If you turn on estimates for your assistant, you are responsible for keeping its
          price list accurate.
        </p>
      </Section>

      <Section heading="5. Acceptable use">
        <p>
          Do not use the service to break the law, to harass or deceive people, to impersonate
          someone, to generate spam, or to attempt to extract the underlying models or another
          customer's data. Do not attempt to circumvent session limits, rate limits or domain
          restrictions. We may suspend an account that does.
        </p>
        <p className="mt-3">
          Never use the service for sexual content involving anyone under 18, or for content that
          sexualizes, grooms or endangers children, including roleplay with or as children. We check
          what you write for your assistant and what visitors send it, refuse and record what breaks
          this rule, and close the account without notice. Where the law requires it, we report it.
        </p>
      </Section>

      <Section heading="6. Accounts and security">
        <p>
          You sign in with Google or with an email address and password through Firebase
          Authentication. Keep your credentials secure; you are responsible for activity under your
          account. Tell us promptly at {SUPPORT_EMAIL} if you believe it has been compromised.
        </p>
        <p>
          You can invite other people to your account and give each one a role. What they do there
          within their role, such as changing an assistant, answering your customers or connecting an
          integration, is done on your behalf, and you are responsible for it as if you had done it
          yourself. An account's owners are responsible for its plans and payments, and you can
          remove anyone you invited at any time.
        </p>
      </Section>

      <Section heading="7. Plans, billing and auto-renewal">
        <p>
          An account can hold {FREE_AGENT_LIMIT === 1 ? "one assistant" : `up to ${FREE_AGENT_LIMIT} assistants`} on the free plan. A free assistant
          answers up to {FREE_ANSWERS_PER_MONTH} questions per calendar month in its time zone,
          across all of its visitors, and up to {FREE_QUESTIONS_PER_SESSION} in any one
          conversation; once a month's answers are used, it tells visitors to contact you directly
          until the next month begins. A free assistant hands visitors to you by email and shows
          its conversations from the last {FREE_HISTORY_DAYS} days.
        </p>
        <p>
          Paid plans are priced per assistant, per month, and billed through Stripe:{" "}
          {PAID_PLAN_IDS.map((id, index) => (
            <span key={id}>
              {index > 0 && (index === PAID_PLAN_IDS.length - 1 ? " and " : "; ")}
              {PLANS[id].name} at {usd(PLANS[id].priceUsd)}, including {answers(PLANS[id].includedAnswers)} answers a
              month, then {usd(PLANS[id].overagePer1000Usd)} per 1,000 extra
            </span>
          ))}
          . {PLANS[EARLY_ACCESS.plan].name} subscriptions started before {EARLY_ACCESS.untilLabel} keep the early access
          price of {usd(EARLY_ACCESS.priceUsd)} a month for as long as they stay on {PLANS[EARLY_ACCESS.plan].name}; from then on, new{" "}
          {PLANS[EARLY_ACCESS.plan].name} subscriptions are {usd(EARLY_ACCESS.laterPriceUsd)} a month. A paid plan's answers are counted per billing period, from the day the plan starts or
          renews until its next renewal. Answers beyond a plan's allowance are charged after the
          period ends: on your next invoice while the plan continues, or on a final invoice when it
          has ended. Each paid assistant has a spending limit on extra answers per billing period,
          which you can change or remove at any time; unless you change it, it equals the plan's
          monthly price. Once the limit is reached, the assistant tells visitors to contact you
          directly until the plan renews. Paid assistants can
          also have a higher limit per conversation, book appointments, hand visitors to you in
          Slack, Discord or through a webhook, take part in live chat, keep their whole conversation
          history and connect your own API, and do not count
          toward the free allowance. {ENTERPRISE.name} plans, from {usd(ENTERPRISE.fromUsd)} a month,
          are agreed with us in writing and invoiced on the terms agreed.
        </p>
        <p>
          <strong>Paid plans renew automatically each month</strong> until canceled, and the card on
          file is charged at the start of each period. Changing an assistant's plan takes effect
          straight away, with the difference for the rest of the period charged or credited. You can cancel any assistant at any time from
          the billing page; cancellation takes effect at the end of the period you have already paid
          for, and the assistant reverts to the free tier rather than being deleted. Except where the
          law requires otherwise, payments already made are non-refundable.
        </p>
        <p>
          Prices are in US dollars and exclude any taxes we are required to collect. We may change
          prices with at least 30 days' notice; a change never applies to a period you have already
          paid for.
        </p>
      </Section>

      <Section heading="8. Development time">
        <p>
          Custom integration work, where we build something your assistant cannot do out of the box,
          is coming soon and is not yet available to order. When it opens, each job will be quoted at
          a fixed price after we have read your request, and nothing is charged until you have seen
          the quote and accepted it.
        </p>
        <p>
          If you bought a block of development hours before we moved to quotes, those hours are
          still yours: they cover scoping, implementation and handover, we will confirm the scope in
          writing before work starts, and if we cannot agree a scope we will refund the unused hours.
          Unused hours do not expire but are not transferable to another customer.
        </p>
      </Section>

      <Section heading="9. Third-party services">
        <p>
          {PRODUCT_NAME} runs on Google Cloud Platform and Firebase, payments are processed by Stripe,
          and email is sent through Brevo. Services you connect your assistant to, such as a booking
          tool, Slack or Discord, are run by their own providers. Your use of the service is also subject to
          their terms, and their availability is outside our control.
        </p>
      </Section>

      <Section heading="10. Intellectual property">
        <p>
          The service, the embed widget and everything we build to run them remain ours. These terms
          grant you a license to use the service, not ownership of it.
        </p>
      </Section>

      <Section heading="11. Service availability">
        <p>
          We do not promise uninterrupted service. Provisioning an assistant depends on Google Cloud
          and can take from minutes to hours, and indexing a large website is slower still. We may
          change or discontinue features, and will give reasonable notice before removing something
          you are paying for.
        </p>
      </Section>

      <Section heading="12. Limitation of liability">
        <p>
          To the fullest extent the law allows, {COMPANY_LEGAL_NAME} is not liable for indirect,
          incidental, special or consequential damages, or for lost profits, revenue or data. Our
          total liability for any claim is limited to the amount you paid us in the twelve months
          before it arose. The service is provided "as is", without warranties of any kind.
        </p>
      </Section>

      <Section heading="13. Termination">
        <p>
          You can stop using the service and archive your assistants at any time. We may suspend or
          terminate an account for breach of these terms, non-payment, or abuse. On termination we
          will make your stored content available for export for 30 days before deleting it.
        </p>
      </Section>

      <Section heading="14. Changes to these terms">
        <p>
          We may update these terms. Material changes will be announced by email or in the dashboard
          before they take effect, and continuing to use the service afterward means you accept
          them.
        </p>
      </Section>

      <Section heading="15. Governing law">
        <p>
          These terms are governed by the laws of the State of Florida, United States, without regard
          to its conflict-of-law rules. The courts of Miami-Dade County, Florida have exclusive
          jurisdiction.
        </p>
      </Section>

      <Section heading="16. Contact">
        <p>
          {COMPANY_LEGAL_NAME}
          <br />
          {COMPANY_ADDRESS}
          <br />
          <a className="text-brand-700 underline" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
        </p>
      </Section>
    </LegalPage>
  );
}
